import base, { SUFFIXES, mapGroupIdsAndValues, getValidator, VALIDATOR_TYPES } from "./_elementBase";
import addressSubElementLayout from "./addressSubElementLayout.vue";
import { isModuleRegistered } from "SharedStores/util";
import { PROJECT_FORM_NAMESPACE, PROJECT_FORM_TYPES } from "../ProjectForm/projectFormStore";
import { getElementsFromDataFieldId } from "../ProjectForm/ProjectElements/dependencyUtils";
import validationRenderHelper from "Validation/_validation-error-helper.vue";
import { FORM_TYPES } from "SharedStores/Form/formStore";
import { KEYS, NAMESPACE } from "./i18n/addressSubElement.i18n";
import { mapI18n } from "Util/i18n";
import { ELEMENT_PROPERTY_NAME } from "Types/elementTypes";
import { PROGRAM_TYPES } from "SharedStores/Program/programStore";

export default {
    components: { "address-sub-element-layout": addressSubElementLayout, validationRenderHelper },
    extends: base,
    props: {
        element: { type: Object, required: true }
    },
    data: function () {
        return {
            setSameAsProcessing: false,
            addressValidationResult: {
                line1: null,
                city: null,
                state: null,
                zipcode: null
            }
        };
    },
    computed: {
        allowSameAs() {
            return this.getBooleanPropertyValue(ELEMENT_PROPERTY_NAME.AllowSameAs) && this.sameAsOptions.length > 0;
        },
        readonly() {
            const hasSameAsValue = this.allowSameAs && this.sameAsValue;
            return Boolean(hasSameAsValue || this.baseReadonly);
        },
        hasLocationSubElementDataField() {
            return Boolean(this.element.Properties["locationsubelementid"]);
        },
        sameAsValue() {
            if (!isModuleRegistered(PROJECT_FORM_NAMESPACE, this.$store.state)) {
                return "";
            }

            const projectElement = this.$store.getters[PROJECT_FORM_TYPES.getters.projectElementFromId](
                this.element.PublicId
            );
            if (!projectElement.FieldDependencies || projectElement.FieldDependencies.length <= 0) {
                return "";
            }
            // need to filter out the any dependencies on the state for this element so that
            // we dont get a false positive when this element has a county dropdown
            const sameAsDependencies = projectElement.FieldDependencies.filter((fd) => fd !== this.stateId);
            if (!sameAsDependencies || sameAsDependencies.length <= 0) {
                return "";
            }
            const dependencyDataFieldId = sameAsDependencies[0];
            const { formElement: dependencyFormElement } = getElementsFromDataFieldId(
                this.$store.getters,
                dependencyDataFieldId
            );

            return dependencyFormElement.PublicId;
        },
        sameAsOptions() {
            return this.element.AuxiliaryModel.potentialSameAs;
        },
        addressModel() {
            const model = {
                line1: this.line1,
                line1Id: this.line1Id,
                line2: this.line2,
                line2Id: this.line2Id,
                city: this.city,
                cityId: this.cityId,
                state: this.state,
                stateId: this.stateId,
                zipcode: this.zipcode,
                zipcodeId: this.zipcodeId,
                usStates: this.$store.getters[FORM_TYPES.getters.USStates],
                caProvinces: this.$store.getters[FORM_TYPES.getters.CAProvinces]
            };
            if (this.getBooleanPropertyValue(ELEMENT_PROPERTY_NAME.ShowCounty) && this.state && this.countyId) {
                const countyOptions = this.$store.getters[FORM_TYPES.getters.countiesFromStateShortName](this.state);
                //only set up and show county if we actually have counties to show (e.g. for CA we don't)
                if (countyOptions && countyOptions.length > 0) {
                    model.showCounty = true;
                    model.countyId = this.countyId;
                    model.county = this.county;
                    model.countyOptions = countyOptions;
                }
            }
            return model;
        },
        isAddressRequired() {
            return this.isContactElement ? this.requiredSubfields.Address : this.required;
        },
        addressValidators() {
            let zipOptions = [];
            let reqValidator = null;
            if (this.isAddressRequired) {
                reqValidator = getValidator(this.$app, [{ type: VALIDATOR_TYPES.required }]);
                zipOptions.push({ type: VALIDATOR_TYPES.required });
            }
            if (this.state) {
                switch (this.country) {
                    case 1: // US
                        zipOptions = [
                            ...zipOptions,
                            {
                                type: VALIDATOR_TYPES.positiveNumber,
                                overrideMsg: this.zipCodeDigitsMessage
                            },
                            {
                                type: VALIDATOR_TYPES.minLength,
                                minLength: 5,
                                overrideMsg: this.zipCodeLengthValidationMessage
                            },
                            {
                                type: VALIDATOR_TYPES.maxLength,
                                maxLength: 5,
                                overrideMsg: this.zipCodeLengthValidationMessage
                            }
                        ];
                        break;
                    case 2: // CAN
                        zipOptions = [
                            ...zipOptions,
                            {
                                type: VALIDATOR_TYPES.minLength,
                                minLength: 3,
                                overrideMsg: this.postalCodeLengthValidationMessage
                            },
                            {
                                type: VALIDATOR_TYPES.maxLength,
                                maxLength: 7,
                                overrideMsg: this.postalCodeLengthValidationMessage
                            },
                            {
                                type: VALIDATOR_TYPES.regEx,
                                regEx: /^[a-zA-Z0-9 ]{3,7}$/,
                                errorMsg: this.postalCodeDigitsMessage
                            }
                        ];
                        break;
                }
            }
            const zipVal = getValidator(this.$app, zipOptions);

            return {
                line1: reqValidator,
                city: reqValidator,
                state: reqValidator,
                zipcode: zipVal,
                county: reqValidator
            };
        },
        isContactElement() {
            return this.element.ClassName === "ContactElement";
        },
        testRole() {
            const classNamePrefix = this.isContactElement ? "contact" : "address";
            return {
                first: `${classNamePrefix}-first-element`,
                last: `${classNamePrefix}-last-element`,
                company: `${classNamePrefix}-company-element`,
                line1: `${classNamePrefix}-line1-element`,
                line2: `${classNamePrefix}-line2-element`,
                city: `${classNamePrefix}-city-element`,
                state: "state-select",
                zip: `${classNamePrefix}-zip-element`,
                email: `${classNamePrefix}-email-element`,
                phone: `${classNamePrefix}-phone-element`
            };
        },
        subElementLayoutModel() {
            const addressValidationErrors = [];
            const missingAddressPieces = [];
            if (this.addressValidationResult.line1 && this.addressValidationResult.line1.hasError) {
                missingAddressPieces.push(this.streetLabel);
            }
            if (this.addressValidationResult.city && this.addressValidationResult.city.hasError) {
                missingAddressPieces.push(this.cityLabel);
            }
            if (this.addressValidationResult.state && this.addressValidationResult.state.hasError) {
                switch (this.$app.i18n.locale) {
                    case "fr-CA": // CAN
                    case "en-CA": // CAN
                        missingAddressPieces.push(this.provinceLabel);
                        break;
                    default:
                        missingAddressPieces.push(this.stateLabel);
                        break;
                }
            }
            if (this.addressValidationResult.county && this.addressValidationResult.county.hasError) {
                missingAddressPieces.push(this.countyLabel);
            }
            if (
                this.addressValidationResult.zipcode &&
                this.addressValidationResult.zipcode.hasError &&
                this.addressValidationResult.zipcode.type === VALIDATOR_TYPES.required
            ) {
                switch (this.$app.i18n.locale) {
                    case "fr-CA": // CAN
                    case "en-CA": // CAN
                        if (this.state && this.country === 1 /* US */) {
                            missingAddressPieces.push(this.zipCodeLabel);
                        } else {
                            missingAddressPieces.push(this.postalCodeLabel);
                        }
                        break;
                    default:
                        if (this.state && this.country === 2 /* CAN */) {
                            missingAddressPieces.push(this.postalCodeLabel);
                        } else {
                            missingAddressPieces.push(this.zipCodeLabel);
                        }
                        break;
                }
            }

            if (missingAddressPieces.length > 0) {
                addressValidationErrors.push(
                    this.res({
                        namespace: NAMESPACE,
                        key: KEYS.requiredFieldsMessage,
                        values: { requiredFields: missingAddressPieces.join(", ") }
                    })
                );
            }

            if (
                this.addressValidationResult.zipcode &&
                this.addressValidationResult.zipcode.hasError &&
                this.addressValidationResult.zipcode.type !== VALIDATOR_TYPES.required
            ) {
                addressValidationErrors.push(this.addressValidationResult.zipcode.error);
            }
            return {
                element: this.element,
                useSubLabel: this.isContactElement,
                label: this.label,
                required: this.isAddressRequired,
                showHelp: this.showHelp,
                showHelpLinkOnly: this.showHelpLinkOnly,
                readonly: this.readonly,
                isInFormEditor: this.isInFormEditor,
                model: this.addressModel,
                responsiveHelpers: this.responsiveHelpers,
                allowSameAs: this.allowSameAs,
                sameAsValue: this.sameAsValue,
                sameAsOptions: this.sameAsOptions,
                disableSameAsDropDown: this.disableSameAsDropDown,
                validator: this.addressValidators,
                addressValidationErrors,
                testRole: this.testRole,
                hasLocationSubElementDataField: this.hasLocationSubElementDataField,
                useAutocomplete: this.useAutocomplete,
                useLocationFilter: this.useLocationFilter,
                includeCounty: this.includeCounty
            };
        },
        disableSameAsDropDown() {
            return this.setSameAsProcessing || this.baseReadonly;
        },
        ...mapGroupIdsAndValues({
            line1: SUFFIXES.Address.Line1,
            line2: SUFFIXES.Address.Line2,
            city: SUFFIXES.Address.City,
            state: SUFFIXES.Address.State,
            zipcode: SUFFIXES.Address.ZipCode,
            county: SUFFIXES.Address.County
        }),
        country() {
            return this.$store.getters[FORM_TYPES.getters.countryFromState](this.state);
        },
        useAutocomplete() {
            // if form element level autocomplete option is undefined, should
            // default to using the program level option. Form element level option will be
            // undefined if the form element has not been edited with autocomplete enabled
            // at the program level.
            return (
                this.element.AuxiliaryModel.useAddressAutocomplete &&
                (this.getBooleanPropertyValue(ELEMENT_PROPERTY_NAME.EnableAutocomplete) ||
                    this.element.Properties[ELEMENT_PROPERTY_NAME.EnableAutocomplete] === undefined)
            );
        },
        useLocationFilter() {
            return (
                this.$store.getters[PROGRAM_TYPES.getters.getUseLocationFilter] &&
                (this.getBooleanPropertyValue(ELEMENT_PROPERTY_NAME.UseAddressAutocompleteFilter) ||
                    this.element.Properties[ELEMENT_PROPERTY_NAME.UseAddressAutocompleteFilter] === undefined)
            );
        },
        includeCounty() {
            return this.getBooleanPropertyValue(ELEMENT_PROPERTY_NAME.ShowCounty);
        },
        ...mapI18n(
            {
                ...KEYS,
                requiredFieldsMessage(fields) {
                    return { key: KEYS.header, values: { requiredFields: fields } };
                }
            },
            NAMESPACE
        )
    },
    methods: {
        onAddressValidated(prop, validationResult) {
            this.addressValidationResult[prop] = validationResult;
        },
        async sameAsSelected(value) {
            this.setSameAsProcessing = true;
            await this.$store.dispatch(
                PROJECT_FORM_TYPES.actions.setSameAs,
                { destElementId: this.element.PublicId, sourceElementId: value },
                { root: true }
            );
            this.setSameAsProcessing = false;
        }
    }
};
